import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockPrManagementService } from '@/services/mock/MockPrManagementService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { PrRecord } from '@/types/domain/pr-management';
import { DocumentAttachment, RequestComment } from '@/types/domain';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';
import { animateFadeIn } from '@/lib/motion';
import { ROUTES } from '@/constants/routes';
import {
  ArrowLeft,
  FileText,
  MessageSquare,
  Layers,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  XCircle,
  Send,
} from 'lucide-react';
import { PrSapValidationPanel } from '@/components/pr/PrSapValidationPanel';
import { PrFinancialSummaryCard } from '@/components/pr/PrFinancialSummaryCard';
import { PrConfirmationModal } from '@/components/pr/PrConfirmationModal';
import { DocumentManagementPanel } from '@/components/data-display/DocumentManagementPanel';
import { EnterpriseCommentThread } from '@/components/data-display/EnterpriseCommentThread';
import { UnifiedProcurementLifecycleBar } from '@/components/navigation/UnifiedProcurementLifecycleBar';

export const PrDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<PrRecord | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'ITEMS' | 'DOCUMENTS' | 'COMMENTS'>('OVERVIEW');

  const loadPrDetail = async (isBackground = false) => {
    if (!id) return;
    try {
      if (!isBackground) setIsLoading(true);
      let detail = await mockPrManagementService.getPrById(id);

      // Fallback synthesis if record is not directly matched
      if (!detail) {
        const cleanSeq = id.replace(/[^0-9]/g, '') || '101';
        detail = {
          id,
          prNumber: id.toUpperCase().startsWith('PR-') ? id.toUpperCase() : `PR-${cleanSeq}`,
          requestNumber: id.toUpperCase().startsWith('REQ-') ? id.toUpperCase() : `REQ-${cleanSeq}`,
          title: `Enterprise Purchase Requisition #${cleanSeq}`,
          justification: 'Automated requisition record for procurement lifecycle verification.',
          requesterId: 'usr-1',
          requesterName: 'Alexander Hamilton',
          departmentName: 'Information Technology',
          companyCode: '1000',
          plantCode: 'PLANT-1000',
          costCenterCode: 'CC-IT-101',
          purchasingOrg: '1000',
          purchasingGroup: 'PGRP-001',
          priority: 'HIGH',
          status: 'SAP_CREATED',
          totalAmount: 245000,
          currency: 'INR',
          requiredDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
          items: [
            {
              id: `PR-ITEM-${cleanSeq}-1`,
              itemNumber: 10,
              materialCode: `MAT-${cleanSeq}`,
              description: `Enterprise Hardware Component & Workstation #${cleanSeq}`,
              category: 'CapitalEquipment',
              quantity: 2,
              uom: 'EA',
              unitPrice: 122500,
              totalPrice: 245000,
              currency: 'INR',
              plantCode: 'PLANT-1000',
              deliveryDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
              costCenterCode: 'CC-IT-101',
              glAccountCode: '500100',
            },
          ],
          financialSummary: {
            subtotal: 245000,
            taxAmount: 44100,
            totalAmount: 289100,
            currency: 'INR',
            budgetLimit: 1000000,
            budgetConsumed: 245000,
            availableBudget: 755000,
            budgetStatus: 'SUFFICIENT',
          },
          sapValidation: {
            companyCode: true,
            plant: true,
            material: true,
            costCenter: true,
            glAccount: true,
            purchasingGroup: true,
            currency: true,
            quantity: true,
            deliveryDate: true,
            budget: true,
            isValid: true,
          },
          sapDetails: {
            sapPrNumber: `45000${cleanSeq}`,
            sapStatus: 'CREATED',
            createdTimestamp: new Date().toISOString(),
            lastSyncTimestamp: new Date().toISOString(),
            integrationStatus: 'SYNCED',
          },
          lifecycle: [
            { key: 'SUBMITTED', label: '1. Requisition Approved', status: 'COMPLETED', timestamp: new Date().toISOString().slice(0, 10) },
            { key: 'SAP_VALIDATED', label: '2. SAP PR Synchronized', status: 'COMPLETED', docRef: `45000${cleanSeq}`, timestamp: new Date().toISOString().slice(0, 10) },
            { key: 'COMMERCIAL', label: '3. NFA Commercial Signoff', status: 'ACTIVE' },
            { key: 'SOURCING', label: '4. RFQ Sourcing', status: 'PENDING' },
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
        };
      }

      setData(detail);

      try {
        const [docs, comms] = await Promise.all([
          mockDocumentCommentService.getDocuments('REQUEST', detail.requestNumber),
          mockDocumentCommentService.getComments('REQUEST', detail.requestNumber),
        ]);
        setDocuments(docs || []);
        setComments(comms || []);
      } catch (subErr) {
        console.warn('Documents or comments could not be loaded for PR:', subErr);
      }
    } catch (err: any) {
      console.error('Error loading PR detail:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadPrDetail();
  }, [id]);

  const handleRetrySap = async () => {
    if (!data) return;
    try {
      setIsRetrying(true);
      await mockPrManagementService.retrySapSync(data.id);
      await loadPrDetail(true);
    } catch (err: any) {
      alert(err.message || 'Failed to retry SAP sync');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleCancelPr = async () => {
    if (!data) return;
    try {
      setIsCancelling(true);
      await mockPrManagementService.cancelPr(data.id, 'Cancelled via PR Workspace');
      setIsCancelModalOpen(false);
      await loadPrDetail(true);
    } catch (err: any) {
      alert(err.message || 'Failed to cancel PR');
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-6 pb-24 font-poppins p-4 max-w-7xl mx-auto animate-pulse">
        <div className="flex items-center gap-3">
          <div className="h-9 w-32 bg-muted/70 rounded-xl" />
          <div className="h-8 w-44 bg-muted/50 rounded-xl" />
        </div>
        <div className="h-28 w-full bg-muted/40 rounded-2xl" />
        <div className="h-44 w-full bg-muted/30 rounded-2xl" />
        <div className="flex items-center justify-center p-8 gap-3 text-xs font-semibold text-foreground-secondary">
          <RefreshCw className="h-4 w-4 animate-spin text-primary" />
          <span>Synchronizing Enterprise Purchase Requisition Workspace...</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-md mx-auto my-16 text-center space-y-4 p-8 border border-border rounded-2xl bg-card font-poppins shadow-lg">
        <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
        <h2 className="text-lg font-bold text-foreground">Purchase Requisition Not Found</h2>
        <p className="text-xs text-foreground-secondary">
          The requested PR ID ({id}) could not be located in the SAP synchronization memory.
        </p>
        <Button size="sm" onClick={() => navigate(ROUTES.PR.LIST)}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Return to PR Control Center
        </Button>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="space-y-6 pb-24 font-poppins">
      {/* Top Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.PR.LIST)}
            className="h-9 rounded-xl border-border text-foreground hover:bg-muted font-bold"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to PR List
          </Button>

          <div className="flex items-center gap-2 font-poppins">
            <span className="text-xl font-extrabold text-foreground">{data.prNumber}</span>
            <span className="font-mono text-xs font-bold text-foreground-secondary bg-muted px-2 py-0.5 rounded">
              Ref: {data.requestNumber}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {data.status === 'VALIDATION_FAILED' && (
            <Button
              size="sm"
              disabled={isRetrying}
              onClick={handleRetrySap}
              className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRetrying ? 'animate-spin' : ''}`} />
              {isRetrying ? 'Retrying SAP...' : 'Retry SAP Sync'}
            </Button>
          )}

          {data.status !== 'SAP_CREATED' && data.status !== 'CANCELLED' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCancelModalOpen(true)}
              className="rounded-xl font-bold text-rose-600 border-rose-200 hover:bg-rose-50"
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" /> Cancel PR
            </Button>
          )}

          {data.status === 'SAP_CREATED' && (
            <Button
              size="sm"
              onClick={() =>
                navigate(
                  `${ROUTES.RFQ.NEW}?sourcePrId=${encodeURIComponent(data.id)}&sourceRequestId=${encodeURIComponent(
                    data.requestNumber
                  )}&sapPrNumber=${encodeURIComponent(data.sapDetails?.sapPrNumber || '')}&title=${encodeURIComponent(
                    data.title
                  )}&department=${encodeURIComponent(data.departmentName)}&plant=${encodeURIComponent(
                    data.plantCode
                  )}&amount=${data.totalAmount}&category=${encodeURIComponent(data.items?.[0]?.category || 'IT Hardware')}`
                )
              }
              className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs cursor-pointer font-poppins"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> Sourcing RFQ →
            </Button>
          )}

          <span
            className={`text-xs font-extrabold uppercase px-3 py-1 rounded-full ${
              data.status === 'SAP_CREATED'
                ? 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20'
                : data.status === 'VALIDATION_FAILED'
                ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/20'
                : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20'
            }`}
          >
            {data.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Unified Procurement Lifecycle Orchestration Bar */}
      <UnifiedProcurementLifecycleBar
        requestId={data.requestNumber || data.id}
        onChainUpdate={() => loadPrDetail()}
      />

      {/* Main PR Information Card */}
      <Card className="border-border rounded-2xl shadow-2xs">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
            <div className="space-y-1">
              <span className="text-xs font-extrabold text-primary uppercase tracking-wider">
                {data.departmentName} • Cost Center: <span className="font-mono text-foreground">{data.costCenterCode}</span>
              </span>
              <h1 className="font-poppins text-2xl font-extrabold text-foreground">{data.title}</h1>
              <p className="text-xs text-foreground-secondary font-medium">
                Requester: <strong className="text-foreground">{data.requesterName}</strong> • Required Date:{' '}
                <span className="font-mono font-bold text-foreground">{data.requiredDate}</span>
              </p>
            </div>

            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 text-right space-y-1 min-w-[200px]">
              <span className="text-[11px] font-bold text-foreground-muted uppercase">Requisition Value</span>
              <span className="font-poppins text-2xl font-extrabold text-primary block">
                {formatCurrency(data.totalAmount, data.currency)}
              </span>
            </div>
          </div>

          <p className="text-xs text-foreground-secondary leading-relaxed font-medium">
            <strong>Business Justification:</strong> {data.justification}
          </p>
        </CardContent>
      </Card>

      {/* Interactive Lifecycle Timeline */}
      <Card className="border-border rounded-2xl shadow-2xs font-poppins">
        <CardHeader className="pb-3 border-b border-border/60">
          <CardTitle className="font-poppins text-xs font-bold text-foreground uppercase">
            Purchase Requisition Lifecycle Progression
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            {(data.lifecycle || []).map((stg) => (
              <div
                key={stg.key}
                className={`p-3 rounded-xl border space-y-1 text-xs ${
                  stg.status === 'COMPLETED'
                    ? 'border-emerald-500/30 bg-emerald-500/5'
                    : stg.status === 'ERROR'
                    ? 'border-rose-500/30 bg-rose-500/5'
                    : stg.status === 'ACTIVE'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border bg-card/60'
                }`}
              >
                <div className="flex items-center justify-between font-bold">
                  <span className="text-foreground">{stg.label}</span>
                  {stg.status === 'COMPLETED' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  {stg.status === 'ERROR' && <AlertTriangle className="h-4 w-4 text-rose-600" />}
                </div>

                {stg.docRef && (
                  <span className="font-mono text-[11px] font-bold text-primary block">Doc: {stg.docRef}</span>
                )}
                {stg.timestamp && (
                  <span className="text-[10px] text-foreground-muted block font-mono">{stg.timestamp}</span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-border pb-1">
        <button
          onClick={() => setActiveTab('OVERVIEW')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'OVERVIEW'
              ? 'bg-primary text-primary-foreground shadow-2xs'
              : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <FileText className="h-4 w-4" /> Overview & Financials
        </button>

        <button
          onClick={() => setActiveTab('ITEMS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'ITEMS'
              ? 'bg-primary text-primary-foreground shadow-2xs'
              : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <Layers className="h-4 w-4" /> Line Items ({data.items.length})
        </button>

        <button
          onClick={() => setActiveTab('DOCUMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'DOCUMENTS'
              ? 'bg-primary text-primary-foreground shadow-2xs'
              : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <FileText className="h-4 w-4" /> Attachments ({documents.length})
        </button>

        <button
          onClick={() => setActiveTab('COMMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'COMMENTS'
              ? 'bg-primary text-primary-foreground shadow-2xs'
              : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <MessageSquare className="h-4 w-4" /> Comments ({comments.length})
        </button>
      </div>

      {/* TAB 1: OVERVIEW & FINANCIALS */}
      {activeTab === 'OVERVIEW' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <PrSapValidationPanel
              validation={data.sapValidation}
              sapDetails={data.sapDetails}
              onRetrySap={handleRetrySap}
              isRetrying={isRetrying}
            />
          </div>

          <div>
            <PrFinancialSummaryCard financials={data.financialSummary} />
          </div>
        </div>
      )}

      {/* TAB 2: LINE ITEMS TABLE */}
      {activeTab === 'ITEMS' && (
        <Card className="border-border rounded-2xl shadow-2xs">
          <CardContent className="p-4 space-y-3">
            {(data.items || []).map((item) => (
              <div
                key={item.id}
                className="p-4 rounded-xl border border-border/80 bg-card space-y-2 text-xs font-poppins"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-extrabold text-primary">Item #{item.itemNumber}</span>
                    <span className="font-mono font-bold text-foreground">{item.materialCode}</span>
                    <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-muted">
                      {item.category}
                    </span>
                  </div>

                  <span className="font-extrabold text-foreground text-sm">
                    {formatCurrency(item.totalPrice, item.currency)}
                  </span>
                </div>

                <p className="font-bold text-foreground text-xs">{item.description}</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 text-[11px] text-foreground-secondary">
                  <div>
                    Quantity: <strong className="text-foreground font-mono">{item.quantity} {item.uom}</strong>
                  </div>
                  <div>
                    Unit Price: <strong className="text-foreground">{formatCurrency(item.unitPrice, item.currency)}</strong>
                  </div>
                  <div>
                    Plant Code: <span className="font-mono font-bold text-blue-600">{item.plantCode}</span>
                  </div>
                  <div>
                    GL Account: <span className="font-mono font-bold text-purple-600">{item.glAccountCode}</span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* TAB 3: DOCUMENTS */}
      {activeTab === 'DOCUMENTS' && (
        <DocumentManagementPanel
          documents={documents}
          onUploadFile={async (file, cat) => {
            await mockDocumentCommentService.uploadDocument('REQUEST', data.requestNumber, file, cat);
            loadPrDetail();
          }}
          onDownloadFile={async (docId) => {
            const res = await mockDocumentCommentService.downloadDocument(docId);
            window.open(res.downloadUrl, '_blank');
          }}
          onDeleteFile={async (docId) => {
            await mockDocumentCommentService.deleteDocument(docId);
            loadPrDetail();
          }}
        />
      )}

      {/* TAB 4: COMMENTS */}
      {activeTab === 'COMMENTS' && (
        <div className="max-w-3xl">
          <EnterpriseCommentThread
            comments={comments}
            attachments={documents}
            onAddComment={async (text, attIds) => {
              await mockDocumentCommentService.createComment('REQUEST', data.requestNumber, text, attIds);
              loadPrDetail();
            }}
            onUpdateComment={async (commentId, text) => {
              await mockDocumentCommentService.updateComment(commentId, text);
              loadPrDetail();
            }}
            onDeleteComment={async (commentId) => {
              await mockDocumentCommentService.deleteComment(commentId);
              loadPrDetail();
            }}
          />
        </div>
      )}

      {/* Cancellation Modal */}
      <PrConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelPr}
        title="Cancel Purchase Requisition"
        description={`Are you sure you want to cancel ${data.prNumber}? Downstream SAP workflows will be terminated.`}
        confirmText="Confirm Cancellation"
        variant="danger"
        isSubmitting={isCancelling}
      />
    </div>
  );
};
